import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { canonicalType, ClassificationInput, productDomain } from "@reina/shared";
import type { Brand, Product } from "@reina/shared";
import {
  CodeChip,
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  TableWrap,
  useToast,
} from "../../components";
import { useBrands } from "../../api/catalogue";
import { useAiClassify, useProducts, useUpdateClassification } from "../../api/products";
import { errorMessage, formatInt } from "../../lib/format";
import { numberList, numberOrNull, textOrNull } from "../../lib/form";
import { Pager } from "../../lib/Pager";
import { confidenceLabel, confidenceTone } from "../../lib/tones";
import { useDebounced } from "../../lib/useDebounced";

const PAGE_SIZE = 50;

export function ProductsPage() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [brandId, setBrandId] = useState("");
  const [domain, setDomain] = useState("");
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebounced(search);

  const brands = useBrands();
  const products = useProducts({
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(brandId ? { brandId } : {}),
    ...(domain ? { domain } : {}),
    ...(needsReviewOnly ? { needsReview: true } : {}),
    limit: PAGE_SIZE,
    offset,
  });
  const aiClassify = useAiClassify();
  const items = products.data?.items ?? [];

  const brandById = useMemo(
    () => new Map((brands.data?.items ?? []).map((b) => [b.id, b])),
    [brands.data]
  );

  const [editing, setEditing] = useState<Product | null>(null);

  const resetPage = () => setOffset(0);

  const onAiClassify = async () => {
    try {
      const result = await aiClassify.mutateAsync(200);
      toast.push({
        message: `AI classify: ${result.processed} processed, ${result.updated} updated`,
        tone: "ok",
      });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <>
      <Panel>
        <PanelHead
          title="Products"
          sub={
            products.data
              ? `${formatInt(products.data.total)} canonical line${products.data.total === 1 ? "" : "s"}`
              : "canonical catalogue with parsed comparability attributes"
          }
          actions={
            <Button variant="orange" onClick={() => void onAiClassify()} disabled={aiClassify.isPending}>
              {aiClassify.isPending ? "Classifying…" : "AI classify"}
            </Button>
          }
        />
        <PanelBody>
          <FieldGrid columns={3}>
            <Field label="Search">
              <input
                className="field"
                placeholder="description, supplier code or part number…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  resetPage();
                }}
              />
            </Field>
            <Field label="Brand">
              <select
                className="field"
                value={brandId}
                onChange={(event) => {
                  setBrandId(event.target.value);
                  resetPage();
                }}
              >
                <option value="">All brands</option>
                {(brands.data?.items ?? []).map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Domain">
              <select
                className="field"
                value={domain}
                onChange={(event) => {
                  setDomain(event.target.value);
                  resetPage();
                }}
              >
                <option value="">All domains</option>
                {productDomain.options.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
          </FieldGrid>
          <label className="field-label" style={{ marginTop: 14 }}>
            <span className="result-cell">
              <input
                type="checkbox"
                checked={needsReviewOnly}
                onChange={(event) => {
                  setNeedsReviewOnly(event.target.checked);
                  resetPage();
                }}
              />
              Needs review only
            </span>
          </label>
        </PanelBody>
        {products.isPending ? (
          <PanelBody>
            <EmptyState title="Loading products…" />
          </PanelBody>
        ) : products.isError ? (
          <PanelBody>
            <EmptyState title="Could not load products" hint={errorMessage(products.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No products found"
              hint="Products appear after the first price-list publish — or loosen the filters."
            />
          </PanelBody>
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <th>Supplier code</th>
                  <th>Description</th>
                  <th>Brand</th>
                  <th>Type</th>
                  <th>Domain</th>
                  <th>Size</th>
                  <th>Pack</th>
                  <th>UOM</th>
                  <th>Classification</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {items.map((product) => (
                  <tr
                    key={product.id}
                    onClick={() => setEditing(product)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="code-stack">
                        <CodeChip code={product.iqCode} title="Supplier code" />
                        {product.manPartNumber && (
                          <CodeChip code={product.manPartNumber} dim title="Manufacturer part no." />
                        )}
                      </span>
                    </td>
                    <td>
                      <strong>{product.description}</strong>
                    </td>
                    <td>
                      {product.brandId
                        ? brandById.get(product.brandId)?.name ?? product.brand ?? "—"
                        : product.brand ?? "—"}
                    </td>
                    <td className="mono">{product.canonicalType}</td>
                    <td className="mono">{product.domain}</td>
                    <td className="mono">{product.sizeSignature || "—"}</td>
                    <td className="mono">{product.packQty ?? "—"}</td>
                    <td className="mono">{product.uom ?? "—"}</td>
                    <td>
                      <span className="result-cell">
                        <span className="mono">{product.classificationSource}</span>
                        <Pill tone={confidenceTone[product.confidence]}>
                          {confidenceLabel[product.confidence]}
                        </Pill>
                      </span>
                    </td>
                    <td>
                      {product.needsReview ? (
                        <Pill tone="warn">Needs review</Pill>
                      ) : (
                        <Pill tone="ok">OK</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <Pager
              total={products.data.total}
              limit={PAGE_SIZE}
              offset={offset}
              onOffset={setOffset}
            />
          </>
        )}
      </Panel>
      {editing && (
        <ClassificationModal
          key={editing.id}
          product={editing}
          brands={brands.data?.items ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Edit-classification modal — records a classification override
// ---------------------------------------------------------------------------

function ClassificationModal({
  product,
  brands,
  onClose,
}: {
  product: Product;
  brands: Brand[];
  onClose: () => void;
}) {
  const toast = useToast();
  const update = useUpdateClassification();
  const [sizesText, setSizesText] = useState(product.sizeMm.join(", "));

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof ClassificationInput>, unknown, z.output<typeof ClassificationInput>>({
    resolver: zodResolver(ClassificationInput),
    defaultValues: {
      canonicalType: product.canonicalType,
      domain: product.domain,
      packQty: product.packQty,
      uom: product.uom ?? "",
      brandId: product.brandId ?? "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const sizeMm = numberList(sizesText);
    if (sizeMm.some(Number.isNaN)) {
      toast.push({ message: "Sizes must be numbers, e.g. 15 or 32, 40", tone: "warn" });
      return;
    }
    try {
      await update.mutateAsync({ id: product.id, input: { ...values, sizeMm } });
      toast.push({ message: "Classification corrected", tone: "ok" });
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title="Edit classification"
      sub={product.description}
      onClose={onClose}
      footer={
        <>
          <span className="sub">Corrections re-apply automatically on every future ingest.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="classification-form"
              disabled={isSubmitting}
            >
              Save correction
            </Button>
          </div>
        </>
      }
    >
      <form id="classification-form" className="stack" onSubmit={onSubmit} noValidate>
        <FieldGrid>
          <Field label="Canonical type" error={errors.canonicalType ? "Invalid type" : undefined}>
            <select className="field" autoFocus {...register("canonicalType")}>
              {canonicalType.options.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Domain" error={errors.domain ? "Invalid domain" : undefined}>
            <select className="field" {...register("domain")}>
              {productDomain.options.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sizes (mm)" hint="Comma-separated, e.g. 15 or 32, 40">
            <input
              className="field"
              value={sizesText}
              onChange={(event) => setSizesText(event.target.value)}
            />
          </Field>
          <Field label="Pack qty" error={errors.packQty?.message}>
            <input
              className="field"
              type="number"
              min={1}
              step={1}
              {...register("packQty", { setValueAs: numberOrNull })}
            />
          </Field>
          <Field label="UOM" hint="e.g. EA, LEN, BAG" error={errors.uom?.message}>
            <input className="field" {...register("uom", { setValueAs: textOrNull })} />
          </Field>
          <Field label="Brand" error={errors.brandId ? "Invalid brand" : undefined}>
            <select className="field" {...register("brandId", { setValueAs: textOrNull })}>
              <option value="">No brand</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>
        </FieldGrid>
      </form>
    </Modal>
  );
}
